/**
 * 🐴 HeroMount — composite Hero + Mount visual for the Hero Window
 * (owner directive, Feb 2026).
 *
 * Behavior:
 *   • When a hero has a mount: render the Mount as the ground layer and
 *     overlay the Hero sprite on top as if riding it. Add a Ride / Dismount
 *     toggle (defaults to Mounted) that swaps between the composite and
 *     the lone hero sprite.
 *   • Hover the mount shows a tooltip with the mount's stat line.
 *   • A compact stat chip sits under the pedestal.
 *   • Heroes flagged `foot_only` (e.g. Sloane) never show a mount —
 *     a quiet "Travels on foot" chip renders instead.
 *
 * Visual-only for now (no backend / stable). The HERO_MOUNTS map keyed
 * by hero name picks the equipped mount art + stat sheet.
 */
import { useMemo, useState } from "react";
import Tilt3D from "@/components/Tilt3D";

// ─────────────────────── Mount catalog ───────────────────────
// Add new mounts here as the owner delivers them. Keep art + stats
// strictly to what the owner has already approved.
export const MOUNT_CATALOG = {
  ashstrider_1: {
    id: "ashstrider_1",
    name: "Ashstrider",
    subtitle: "Ember Mount #1",
    art_url:
      "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/cffbea359258a0f9_Ashstrider%2C%20Ember%20Mount%20%231.jpg",
    stats: {
      STR: 10,
      ARM: 10,
      INT: 10,
      END: 10,
      MOVE_SPEED_PCT: 30,
    },
  },
};

// Which hero rides which mount (owner-approved bindings only).
// Heroes marked `foot_only: true` never mount up (e.g. Sloane).
export const HERO_MOUNT_BINDINGS = {
  bram:   { mount_id: "ashstrider_1" },
  sloane: { foot_only: true, note: "Travels on foot" },
};

// Known hero art overrides (owner-provided renders). These take
// precedence over the Codex sprite_url so the exact uploaded art
// is used on the Hero Window.
// NOTE: Bram is NOT the Mage of Dragonious — override is keyed on
// `bram` only. Each hero's class override must be bound explicitly.
export const HERO_ART_OVERRIDES = {
  bram: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/f7cbd1f82ce1603a_Bram%203D-style%20render.webp",
};

function _normName(s) {
  return String(s || "").trim().toLowerCase().split(/[\s,]+/)[0];
}

export function getHeroMountBinding(heroName, className) {
  // Match on hero name first, then class — owner's bindings are per-hero.
  const n = _normName(heroName);
  const c = _normName(className);
  return HERO_MOUNT_BINDINGS[n] || HERO_MOUNT_BINDINGS[c] || null;
}

export function getHeroArtOverride(heroName, className) {
  const n = _normName(heroName);
  const c = _normName(className);
  return HERO_ART_OVERRIDES[n] || HERO_ART_OVERRIDES[c] || null;
}

// ─────────────────────── Component ───────────────────────

export default function HeroMount({ heroName, className, sprite_url }) {
  const binding = useMemo(
    () => getHeroMountBinding(heroName, className),
    [heroName, className]
  );
  const mount = binding?.mount_id ? MOUNT_CATALOG[binding.mount_id] : null;

  const heroArt = useMemo(
    () => getHeroArtOverride(heroName, className) || sprite_url || null,
    [heroName, className, sprite_url]
  );

  const [mounted, setMounted] = useState(Boolean(mount));
  const [brokenHero, setBrokenHero] = useState(false);
  const [brokenMount, setBrokenMount] = useState(false);

  const canShowHero  = heroArt && !brokenHero;
  const canShowMount = mount && !brokenMount && mounted;

  return (
    <div className="flex-1 flex flex-col items-center justify-end pb-5 relative w-full"
         data-testid="hero-mount-stage">
      {/* Mount + Hero composite stage */}
      <div className="relative w-full flex items-end justify-center" style={{ minHeight: "60%" }}>
        {canShowMount && (
          <Tilt3D max={10} scale={1.03} className="max-h-[72%] flex items-end" style={{ maxWidth: "92%" }}>
            <img
              src={mount.art_url}
              alt={mount.name}
              onError={() => setBrokenMount(true)}
              className="max-h-[72vh] object-contain"
              title={_mountTooltip(mount, mounted)}
              data-testid="hero-mount-img"
            />
          </Tilt3D>
        )}
        {canShowHero && (
          <Tilt3D
            max={canShowMount ? 8 : 12}
            scale={canShowMount ? 1.02 : 1.04}
            className={
              canShowMount
                ? "absolute left-1/2 -translate-x-1/2"
                : "max-h-[85%]"
            }
            style={canShowMount ? { bottom: "28%", maxHeight: "62%", maxWidth: "48%" } : undefined}
          >
            <img
              src={heroArt}
              alt={heroName || className || "Hero"}
              onError={() => setBrokenHero(true)}
              className={canShowMount ? "object-contain max-h-[62vh]" : "object-contain max-h-[85vh]"}
              data-testid="hero-art-img"
            />
          </Tilt3D>
        )}
        {!canShowHero && !canShowMount && (
          <HeroFallback className_name={className} />
        )}
        {!canShowHero && canShowMount && (
          <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: "28%" }}>
            <HeroFallback className_name={className} compact />
          </div>
        )}
      </div>

      {/* Mount controls (ride / dismount) + stat chip.
          Owner rule: Mount passives (STR/ARM/INT/END) always apply;
          only Move Speed +X% requires actually riding. */}
      {mount && (
        <div className="mt-2 flex items-center gap-2 flex-wrap justify-center">
          <button
            onClick={() => setMounted((m) => !m)}
            data-testid="hero-mount-toggle"
            className={
              "text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm border transition " +
              (mounted
                ? "border-amber-500 text-amber-200 bg-amber-900/30 hover:border-amber-300"
                : "border-amber-700/50 text-amber-300/70 bg-black/40 hover:text-amber-200")
            }
            title={
              mounted
                ? "Dismount — passives (STR/ARM/INT/END) stay active; Move Speed bonus drops."
                : "Ride — restores the Move Speed bonus. Passives are always on."
            }
          >
            {mounted ? "Dismount" : "Ride"}
          </button>
          <MountStatChip mount={mount} riding={mounted} />
        </div>
      )}

      {binding?.foot_only && (
        <div
          className="mt-2 text-[9px] uppercase tracking-widest text-amber-300/70 border border-amber-800/40 rounded-sm px-2 py-1 bg-black/30"
          data-testid="hero-foot-only-chip"
          title={binding.note || "This hero travels on foot."}
        >
          🚶 Travels on foot
        </div>
      )}
    </div>
  );
}

// Compact stat chip under the pedestal (owner approved).
// Passives are always on; Move Speed only applies while riding.
function MountStatChip({ mount, riding = true }) {
  const s = mount.stats || {};
  return (
    <div
      className="text-[9px] uppercase tracking-widest text-amber-200/90 border border-amber-700/40 rounded-sm px-2 py-1 bg-black/40 flex items-center gap-1.5 font-mono"
      title={_mountTooltip(mount, riding)}
      data-testid="hero-mount-stat-chip"
    >
      <span className="text-amber-300 font-bold normal-case tracking-normal">
        {mount.name}
      </span>
      <span className="text-amber-700">·</span>
      <span>STR+{s.STR ?? 0}</span>
      <span>ARM+{s.ARM ?? 0}</span>
      <span>INT+{s.INT ?? 0}</span>
      <span>END+{s.END ?? 0}</span>
      <span
        className={riding ? "" : "line-through text-amber-700"}
        title={riding ? "Riding — Move Speed bonus is active" : "Dismounted — Move Speed bonus is paused"}
      >
        SPD+{s.MOVE_SPEED_PCT ?? 0}%
      </span>
    </div>
  );
}

function _mountTooltip(mount, riding = true) {
  const s = mount.stats || {};
  return (
    `${mount.name} — ${mount.subtitle}\n` +
    `Passives (always on): STR +${s.STR ?? 0}, ARM +${s.ARM ?? 0}, INT +${s.INT ?? 0}, END +${s.END ?? 0}\n` +
    `Move Speed +${s.MOVE_SPEED_PCT ?? 0}% — ${riding ? "ACTIVE (riding)" : "paused (dismounted)"}`
  );
}

// Silhouette fallback — kept identical in look to the original window
// so classes without art still render cleanly.
function HeroFallback({ class_name, compact = false }) {
  const scale = compact ? "scale-[0.6]" : "";
  return (
    <div className={`relative flex flex-col items-center ${scale}`} aria-hidden="true"
         data-testid="hero-avatar-silhouette">
      <div className="w-14 h-14 rounded-full bg-[#9b7557]" />
      <div className="w-24 h-28 bg-[#9b7557] rounded-md mt-1 relative">
        <span className="absolute -left-5 top-2 w-5 h-14 bg-[#9b7557] rounded-md" />
        <span className="absolute -right-5 top-2 w-5 h-14 bg-[#9b7557] rounded-md" />
      </div>
      <div className="flex gap-1 mt-1">
        <span className="w-5 h-7 bg-[#9b7557] rounded-sm" />
        <span className="w-5 h-7 bg-[#9b7557] rounded-sm" />
      </div>
      {class_name && !compact && (
        <div className="absolute bottom-0 translate-y-4 text-[9px] uppercase tracking-widest text-neutral-500">
          {class_name}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Mount drawer tab ───────────────────────

/** Full mount panel used by the Hero Window's MOUNT drawer tab. */
export function MountTab({ heroName, className }) {
  const binding = getHeroMountBinding(heroName, className);
  const mount = binding?.mount_id ? MOUNT_CATALOG[binding.mount_id] : null;

  if (binding?.foot_only) {
    return (
      <div className="space-y-2 text-[11px] text-amber-300/70" data-testid="drawer-mount-foot-only">
        <div className="text-[10px] uppercase tracking-widest">On foot</div>
        <div className="text-[11px] text-amber-200 italic">
          {heroName || "This hero"} travels on foot and does not ride a mount.
        </div>
      </div>
    );
  }
  if (!mount) {
    return (
      <div className="space-y-2 text-[11px] text-amber-300/70" data-testid="drawer-mount-empty">
        <div className="text-[10px] uppercase tracking-widest">No mount equipped</div>
        <div className="text-[11px] italic">
          Earn an Ember Mount from the Phoenix Tiers path.
        </div>
      </div>
    );
  }
  const s = mount.stats || {};
  return (
    <div className="space-y-2 text-[11px]" data-testid="drawer-mount">
      <div className="flex items-center gap-2">
        <Tilt3D max={16} scale={1.08} className="w-14 h-14 relative">
          <img src={mount.art_url} alt={mount.name}
               className="w-14 h-14 object-cover rounded-sm border border-amber-700/40"
               data-testid="drawer-mount-art" />
        </Tilt3D>
        <div>
          <div className="text-amber-300 font-bold">{mount.name}</div>
          <div className="text-amber-300/70 text-[10px] uppercase tracking-widest">
            {mount.subtitle}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-amber-100">
        <span>STR <span className="text-emerald-400">+{s.STR ?? 0}</span></span>
        <span>ARM <span className="text-emerald-400">+{s.ARM ?? 0}</span></span>
        <span>INT <span className="text-emerald-400">+{s.INT ?? 0}</span></span>
        <span>END <span className="text-emerald-400">+{s.END ?? 0}</span></span>
        <span className="col-span-2">
          Move Speed <span className="text-emerald-400">+{s.MOVE_SPEED_PCT ?? 0}%</span>
          <span className="text-amber-300/70 normal-case italic ml-1">(while riding)</span>
        </span>
      </div>
      <div className="text-[10px] text-neutral-500 italic">
        Passives (STR / ARM / INT / END) stay active whether mounted or on foot — only
        Move Speed pauses when you dismount. Move Speed is a single % from the mount
        you're currently riding and never stacks across mounts.
      </div>
    </div>
  );
}
